import { useRef, useCallback } from 'react'

interface BalloonAnimationOptions {
  duration?: number
  count?: number
  emojis?: string[]
}

export const useBalloonAnimation = (options: BalloonAnimationOptions = {}) => {
  const {
    duration = 6000,
    count,
    emojis = ['🎉', '🚀', '⭐', '✨', '💜', '🎊']
  } = options
  
  const activeElementsRef = useRef<HTMLDivElement[]>([])
  const styleElementRef = useRef<HTMLStyleElement | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const cleanup = useCallback(() => {
    // Remove all active balloon elements
    activeElementsRef.current.forEach((el) => {
      if (el.parentNode) {
        el.parentNode.removeChild(el)
      }
    })
    activeElementsRef.current = []

    // Remove style element
    if (styleElementRef.current && styleElementRef.current.parentNode) {
      styleElementRef.current.parentNode.removeChild(styleElementRef.current)
      styleElementRef.current = null
    }

    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const launchBalloons = useCallback(() => {
    // Clean up any existing animation
    cleanup()

    // Create and inject styles
    const style = document.createElement('style')
    style.id = 'balloon-keyframes'
    style.textContent = `
      @keyframes balloon-float {
        0% { transform: translateY(0) scale(0.7); opacity: 0; }
        10% { opacity: 1; }
        75% { opacity: 1; }
        100% { transform: translateY(-110vh) scale(1); opacity: 0; }
      }
      @keyframes balloon-jiggle-0 {
        0%, 100% { translate: 0 0; rotate: -2deg; }
        50% { translate: 8px 0; rotate: 2deg; }
      }
      @keyframes balloon-jiggle-1 {
        0%, 100% { translate: 0 0; rotate: 1deg; }
        50% { translate: -6px 0; rotate: -2deg; }
      }
      @keyframes balloon-jiggle-2 {
        0%, 100% { translate: 4px 0; rotate: -1deg; }
        50% { translate: -4px 0; rotate: 1.5deg; }
      }
    `
    document.head.appendChild(style)
    styleElementRef.current = style

    // Create balloon elements
    const balloonCount = count ?? (window.innerWidth < 768 ? 15 : 30)
    const elements: HTMLDivElement[] = []

    for (let i = 0; i < balloonCount; i++) {
      const el = document.createElement('div')
      el.textContent = emojis[Math.floor(Math.random() * emojis.length)]
      el.setAttribute('data-balloon', 'true') // For easier identification
      
      const jiggle = `balloon-jiggle-${i % 3}`
      const floatDuration = 3 + Math.random() * 2
      const jiggleDuration = 0.8 + Math.random() * 0.6
      const delay = Math.random() * 0.8
      const size = 80 + Math.random() * 60
      const left = Math.random() * 90
      
      el.style.cssText = `
        position: fixed;
        font-size: ${size}px;
        left: ${left}vw;
        bottom: -150px;
        z-index: 9999;
        pointer-events: none;
        animation: balloon-float ${floatDuration}s ease-out forwards, ${jiggle} ${jiggleDuration}s ease-in-out infinite;
        animation-delay: ${delay}s;
        opacity: 0;
      `
      
      document.body.appendChild(el)
      elements.push(el)
    }

    activeElementsRef.current = elements

    // Schedule cleanup
    timeoutRef.current = setTimeout(cleanup, duration)
  }, [cleanup, duration, count, emojis])

  return {
    launchBalloons,
    cleanup
  }
}